import React, { useState, useMemo } from "react";
import {
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  TextField,
  Avatar,
  AvatarGroup,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import "./index.css";
import MaterialButton from "../MaterialButton";
import MaterialTypography from "../MaterialTypography";
import TableSearch from "./tableSearch";

const Table = ({
  columns,
  data,
  actions,
  pagination,
  getRowKey,
  statuses,
  className,
  profile,
  teamAvatar,
  searchColumns,
  noDataLabel,
  handleSearch = () => {},
  isSearch = true,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(() => {
    const initialColumns = columns.map((col) => col.key);
    if (profile) initialColumns.unshift("profile");
    if (teamAvatar) initialColumns.unshift("teamAvatar");
    return initialColumns;
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionRowKey, setActionRowKey] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "N/A";
    const names = name.split(" ");
    return names
      .map((n) => n.charAt(0))
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };
  const handleColumnsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnsClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (columnKey) => {
    setSelectedColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleActionClick = (event, rowKey) => {
    setActionAnchorEl(event.currentTarget);
    setActionRowKey(rowKey);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setActionRowKey(null);
  };

  // New function to handle sort
  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Team Avatar rendering logic embedded within ReusableTable
  const renderTeamAvatar = (members, max = 3) => {
    const validMembers = Array.isArray(members) ? members : [];
    const displayedMembers = validMembers.slice(0, max);
    const extraMembers = validMembers.length - max;

    return (
      <Tooltip title={validMembers.map((m) => m.name).join(", ")}>
        <AvatarGroup
          max={max}
          sx={{ display: "flex", justifyContent: "flex-start" }}
        >
          {displayedMembers.map((member, index) => (
            <Avatar
              key={index}
              src={member.image || ""} // Use image if provided
              alt={member.name || `Member ${index + 1}`}
              sx={{
                width: 24,
                height: 24,
                bgcolor: member.image ? "transparent" : "#2d4080",
                color: "#fff",
                fontSize: 10,
              }}
            >
              {!member.image && member.name
                ? member.name
                    .split(" ")
                    .map((n) => n.charAt(0))
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()
                : null}
            </Avatar>
          ))}
          {extraMembers > 0 && (
            <Avatar
              sx={{
                width: 20,
                height: 20,
                bgcolor: "#2d4080",
                color: "#fff",
                fontSize: 6,
                fontWeight: "bold",
              }}
            >
              +{extraMembers}
            </Avatar>
          )}
        </AvatarGroup>
      </Tooltip>
    );
  };

  const baseColumns = [
    ...(profile
      ? [
          {
            key: "profile",
            label: "Profile",
            dataLabel: "Profile",
            renderCell: (row) => row.customerName || "Unknown",
          },
        ]
      : []),
    ...(teamAvatar
      ? [
          {
            key: "teamAvatar",
            label: "Team",
            dataLabel: "Team",
            renderCell: (row) => renderTeamAvatar(row.teamMembers || []),
          },
        ]
      : []),
    ...columns,
  ];

  const tableColumns = baseColumns.filter((col) =>
    selectedColumns.includes(col.key)
  );

  const filteredAndSortedData = useMemo(() => {
    // First, apply search filter
    let result = data;
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = data.filter((row) =>
        searchColumns.some((colKey) => {
          if (colKey === "profile") {
            return (
              row.customerName &&
              row.customerName.toLowerCase().includes(lowerQuery)
            );
          }
          if (colKey === "teamAvatar") {
            return (
              row.teamMembers &&
              row.teamMembers.some(
                (member) =>
                  member.name && member.name.toLowerCase().includes(lowerQuery)
              )
            );
          }
          const value = row[colKey];
          return (
            value != null && value.toString().toLowerCase().includes(lowerQuery)
          );
        })
      );
    }

    // Then, apply sorting
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === "profile") {
          aValue = a.customerName || "";
          bValue = b.customerName || "";
        } else if (sortConfig.key === "teamAvatar") {
          aValue =
            a.teamMembers && a.teamMembers[0]?.name
              ? a.teamMembers[0].name
              : "";
          bValue =
            b.teamMembers && b.teamMembers[0]?.name
              ? b.teamMembers[0].name
              : "";
        }

        if (aValue == null) aValue = "";
        if (bValue == null) bValue = "";

        aValue = aValue.toString();
        bValue = bValue.toString();

        if (sortConfig.direction === "asc") {
          return aValue.localeCompare(bValue, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        }
        return bValue.localeCompare(aValue, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    }

    return result;
  }, [data, searchQuery, searchColumns, sortConfig]);

  // check plain object (not array / null)
  const isPlainObject = (val) =>
    val !== null && typeof val === "object" && !Array.isArray(val);

  // format object safely for tooltip
  const formatObject = (obj) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const ObjectCell = ({ value }) => {
    if (!isPlainObject(value)) return value;

    return (
      <Tooltip
        title={
          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {formatObject(value)}
          </pre>
        }
        arrow
        placement="right"
      >
        <span
          style={{
            color: "#2d4080",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          See more
        </span>
      </Tooltip>
    );
  };

  return (
    <div className={className}>
      <div className="table-controls">
        <div className="columns-control">
          <MaterialButton
            variant="outlined"
            size="small"
            onClick={handleColumnsClick}
            aria-controls="columns-menu"
            aria-haspopup="true"
            sx={{ borderColor: "#2d4080", color: "#2d4080" }}
          >
            Columns
          </MaterialButton>
          <Menu
            id="columns-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleColumnsClose}
            PaperProps={{
              style: {
                minWidth: 200,
                backgroundColor: "#2a2a2a",
                color: "#fff",
              },
            }}
          >
            {baseColumns.map((column) => (
              <MenuItem
                key={column.key}
                onClick={() => handleColumnToggle(column.key)}
                sx={{ padding: "4px 16px" }}
              >
                <Checkbox
                  checked={selectedColumns.includes(column.key)}
                  sx={{ color: "#fff", "&.Mui-checked": { color: "#2d4080" } }}
                />
                {column.label}
              </MenuItem>
            ))}
          </Menu>
        </div>
        {isSearch && (
          <div className="search-control">
            {/* <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderColor: "#2d4080",
                color: "#2a2a2a",
                "& fieldset": { borderColor: "#2d4080" },
                "&:hover fieldset": { borderColor: "#2d4080" },
              },
            }}
            aria-label="Search table"
          /> */}
            <TableSearch onChange={handleSearch} />
          </div>
        )}
      </div>
      <table className="invoice-table">
        <thead>
          <tr>
            {tableColumns.map((column) => (
              <th
                key={column.key}
                onClick={() => handleSort(column.key)}
                style={{ cursor: "pointer" }}
                aria-sort={
                  sortConfig.key === column.key ? sortConfig.direction : "none"
                }
              >
                <div className="th-content">
                  {column.label}
                  {sortConfig.key === column.key &&
                    (sortConfig.direction === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" className="sort-icon" />
                    ) : (
                      <ArrowDownwardIcon
                        fontSize="small"
                        className="sort-icon"
                      />
                    ))}
                </div>
              </th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedData.length > 0 ? (
            filteredAndSortedData.map((row) => (
              <tr key={getRowKey(row)}>
                {tableColumns.map((column) => (
                  <td key={column.key} data-label={column.dataLabel}>
                    {column.key === "profile" ? (
                      <div className="profile-cell">
                        {row.profile?.image ? (
                          <img
                            src={row.profile.image}
                            alt={`${row.customerName || "Unknown"}'s profile`}
                            className="profile-image"
                          />
                        ) : (
                          <div className="profile-placeholder">
                            {getInitials(row.customerName)}
                          </div>
                        )}
                      </div>
                    ) : column.key === "teamAvatar" ? (
                      column.renderCell(row)
                    ) : column.key === "status" ? (
                      <span
                        className="status"
                        style={
                          statuses.find((s) => s.value === row.status)
                            ?.style || {
                            backgroundColor: "#ccc",
                            color: "#000",
                          }
                        }
                      >
                        {row.status}
                      </span>
                    ) : column.isObject ? (
                      <ObjectCell value={row[column.key]} />
                    ) : column.renderCell ? (
                      column.renderCell(row)
                    ) : (
                      row[column.key]
                    )}
                  </td>
                ))}
                <td className="action-cell">
                  <Tooltip title="Actions">
                    <IconButton
                      onClick={(e) => handleActionClick(e, getRowKey(row))}
                      aria-controls={`action-menu-${getRowKey(row)}`}
                      aria-haspopup="true"
                      className="action-icon"
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id={`action-menu-${getRowKey(row)}`}
                    anchorEl={actionAnchorEl}
                    open={
                      Boolean(actionAnchorEl) && actionRowKey === getRowKey(row)
                    }
                    onClose={handleActionClose}
                    PaperProps={{
                      style: {
                        minWidth: 120,
                        backgroundColor: "#2a2a2a",
                        color: "#fff",
                      },
                    }}
                  >
                    {actions.edit && (
                      <MenuItem
                        onClick={() => {
                          actions.onEdit(row);
                          handleActionClose();
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#2d4080",
                            color: "#fff",
                          },
                        }}
                      >
                        Edit
                      </MenuItem>
                    )}
                    {actions.view && (
                      <MenuItem
                        onClick={() => {
                          actions.onView(row);
                          handleActionClose();
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#2d4080",
                            color: "#fff",
                          },
                        }}
                      >
                        View
                      </MenuItem>
                    )}
                    {actions.delete && (
                      <MenuItem
                        onClick={() => {
                          actions.onDelete(row);
                          handleActionClose();
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#2d4080",
                            color: "#fff",
                          },
                        }}
                      >
                        Delete
                      </MenuItem>
                    )}
                  </Menu>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={tableColumns.length + 1}
                style={{ textAlign: "center" }}
              >
                {selectedColumns.length === 0 ? (
                  <MaterialTypography
                    variant="h4"
                    color="dark"
                    sx={{
                      fontStyle: "italic",
                      fontFamily: '"Dancing Script", cursive',
                    }}
                  >
                    {" "}
                    No columns selected
                  </MaterialTypography>
                ) : (
                  <MaterialTypography
                    variant="h4"
                    color="dark"
                    sx={{
                      fontStyle: "italic",
                      fontFamily: '"Dancing Script", cursive',
                    }}
                  >
                    {noDataLabel}
                  </MaterialTypography>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {pagination.totalPages > 0 && filteredAndSortedData.length > 0 && (
        <div
          className="pagination"
          style={{
            marginTop: "20px",
            textAlign: "right",
            alignItems: "flex-end",
          }}
        >
          <FormControl size="small" sx={{ mx: 1, width: "70px" }}>
            <InputLabel id="page-size-select-label" sx={{ fontSize: "13px" }}>
              Rows
            </InputLabel>
            <Select
              labelId="page-size-select-label"
              id="page-size-select"
              value={pagination.rowsPerPage || 10}
              label="row size"
              sx={{ minWidth: "70px" }}
              onChange={(e) => pagination.onRowsPerPageChange(e.target.value)}
            >
              {(pagination.rowsPerPageOptions || [5, 10, 25, 50]).map(
                (option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            disabled={pagination.currentPage === 1}
            onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
            sx={{ mx: 1 }}
          >
            Previous
          </Button>
          {Array.from({ length: pagination.totalPages }, (_, index) => (
            <Button
              key={index + 1}
              variant={
                pagination.currentPage === index + 1 ? "contained" : "outlined"
              }
              size="small"
              onClick={() => pagination.onPageChange(index + 1)}
              sx={{ mx: 0.5 }}
            >
              {index + 1}
            </Button>
          ))}
          <Button
            variant="outlined"
            size="small"
            disabled={pagination.currentPage === pagination.totalPages}
            onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
            sx={{ mx: 1 }}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default Table;
